import React, { useEffect, useState, useMemo } from "react";
import { Search, RefreshCw, Coins, Archive } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const BALANCE_COLORS = {
  Annual: "#00ff90", Sick: "#f87171", Casual: "#c084fc",
  Unpaid: "#fbbf24", Maternity: "#60a5fa", Paternity: "#34d399",
};

export default function BalancesTab({ onOpenEmployee }) {
  const [balances, setBalances] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState("");

  // Adjuster
  const [adj, setAdj] = useState({ employee_id: "", leave_type: "Annual", delta: 1, reason: "" });
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjMsg,  setAdjMsg]  = useState(null);
  const [adjLog,  setAdjLog]  = useState([]);

  // Bulk credit
  const [bulk, setBulk] = useState({ scope: "ALL", leave_type: "Annual", days: 1, reason: "" });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg,  setBulkMsg]  = useState(null);

  // Carry-over / Lapse / Encashment
  const [opBusy, setOpBusy] = useState(null);
  const [opMsg,  setOpMsg]  = useState(null);

  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/balances`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setBalances(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    if (!search) return balances;
    const q = search.toLowerCase();
    return balances.filter(b =>
      (b.name || "").toLowerCase().includes(q) ||
      (b.employee_id || "").toLowerCase().includes(q) ||
      (b.dept || "").toLowerCase().includes(q)
    );
  }, [balances, search]);

  const applyAdj = async () => {
    if (!adj.employee_id || !adj.reason) {
      setAdjMsg({ type: "error", text: "Employee ID and reason are required." }); return;
    }
    setAdjBusy(true); setAdjMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/balances/adjust`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(adj),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setAdjMsg({ type: "success", text: `✓ Adjusted ${adj.leave_type} for ${adj.employee_id}: ${adj.delta > 0 ? "+" : ""}${adj.delta}d` });
      setAdjLog(l => [{ ...adj, at: new Date().toLocaleTimeString() }, ...l.slice(0, 9)]);
      setAdj(a => ({ ...a, employee_id: "", reason: "" }));
      await load();
    } catch (e) { setAdjMsg({ type: "error", text: e.message }); }
    finally { setAdjBusy(false); }
  };

  const applyBulk = async () => {
    if (!bulk.reason) { setBulkMsg({ type: "error", text: "Reason is required." }); return; }
    setBulkBusy(true); setBulkMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/bulk-credit`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(bulk),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setBulkMsg({ type: "success", text: `✓ Bulk credited ${bulk.days}d ${bulk.leave_type} to ${bulk.scope}` });
      await load();
    } catch (e) { setBulkMsg({ type: "error", text: e.message }); }
    finally { setBulkBusy(false); }
  };

  const runOp = async (op) => {
    setOpBusy(op); setOpMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/${op}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setOpMsg({ type: "success", text: `✓ ${op} completed — ${data.affected || 0} records updated` });
      await load();
    } catch (e) { setOpMsg({ type: "error", text: e.message }); }
    finally { setOpBusy(null); }
  };

  return (
    <div className="space-y-5">

      {/* Year-end operations */}
      <div className={`${card} p-5`}>
        <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Year-End Operations</p>
        <div className="flex flex-wrap gap-3">
          {[
            { op: "carry-over",  label: "Run Carry-Over",  icon: RefreshCw, desc: "Forward unused leave to next year per policy caps",  style: "border-blue-500/25 bg-blue-500/8 text-blue-400/70 hover:bg-blue-500/15 hover:text-blue-400"   },
            { op: "lapse",       label: "Run Lapse",       icon: Archive,   desc: "Expire leave past its expiry date",                   style: "border-orange-500/25 bg-orange-500/8 text-orange-400/70 hover:bg-orange-500/15 hover:text-orange-400" },
            { op: "encashment",  label: "Run Encashment",  icon: Coins,     desc: "Pay out encashable unused leave",                     style: "border-purple-500/25 bg-purple-500/8 text-purple-400/70 hover:bg-purple-500/15 hover:text-purple-400" },
          ].map(({ op, label, icon: Icon, desc, style }) => (
            <div key={op} className="flex-1 min-w-[180px] rounded-xl border border-white/5 bg-white/3 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={13} className="text-white/30" />
                <p className="text-sm text-white/70 font-medium">{label}</p>
              </div>
              <p className="text-xs text-white/25 mb-3">{desc}</p>
              <button onClick={() => runOp(op)} disabled={opBusy !== null}
                className={`text-xs px-3 py-1.5 rounded-lg border disabled:opacity-40 transition-colors ${style}`}>
                {opBusy === op ? "Running…" : label}
              </button>
            </div>
          ))}
        </div>
        {opMsg && <p className={`text-xs mt-3 ${opMsg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{opMsg.text}</p>}
      </div>

      {/* Bulk credit + Single adjuster */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Bulk credit */}
        <div className={`${card} p-5 space-y-3`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Bulk Credit Leave</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Scope</label>
              <input className={`${inputCls} w-full`} value={bulk.scope} onChange={e => setBulk(b => ({ ...b, scope: e.target.value }))} placeholder="ALL / dept / location" /></div>
            <div><label className={labelCls}>Leave Type</label>
              <select className={`${inputCls} w-full`} value={bulk.leave_type} onChange={e => setBulk(b => ({ ...b, leave_type: e.target.value }))}>
                {["Annual","Sick","Casual","Unpaid","Maternity","Paternity"].map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Days to Credit</label>
              <input type="number" step="0.5" className={`${inputCls} w-full`} value={bulk.days} onChange={e => setBulk(b => ({ ...b, days: parseFloat(e.target.value) }))} /></div>
            <div><label className={labelCls}>Reason</label>
              <input className={`${inputCls} w-full`} value={bulk.reason} onChange={e => setBulk(b => ({ ...b, reason: e.target.value }))} placeholder="e.g. Q3 bonus leave" /></div>
          </div>
          {bulkMsg && <p className={`text-xs ${bulkMsg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{bulkMsg.text}</p>}
          <button onClick={applyBulk} disabled={bulkBusy}
            className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
            {bulkBusy ? "Crediting…" : "Apply Bulk Credit"}
          </button>
        </div>

        {/* Single adjuster */}
        <div className={`${card} p-5 space-y-3`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Single Balance Adjustment</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Employee ID</label>
              <input className={`${inputCls} w-full`} value={adj.employee_id} onChange={e => setAdj(a => ({ ...a, employee_id: e.target.value }))} placeholder="e.g. E001" /></div>
            <div><label className={labelCls}>Leave Type</label>
              <select className={`${inputCls} w-full`} value={adj.leave_type} onChange={e => setAdj(a => ({ ...a, leave_type: e.target.value }))}>
                {["Annual","Sick","Casual","Unpaid","Maternity","Paternity"].map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Adjustment (days)</label>
              <input type="number" step="0.5" className={`${inputCls} w-full`} value={adj.delta} onChange={e => setAdj(a => ({ ...a, delta: parseFloat(e.target.value) }))} /></div>
            <div><label className={labelCls}>Reason</label>
              <input className={`${inputCls} w-full`} value={adj.reason} onChange={e => setAdj(a => ({ ...a, reason: e.target.value }))} placeholder="Required for audit" /></div>
          </div>
          {adjMsg && <p className={`text-xs ${adjMsg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{adjMsg.text}</p>}
          <button onClick={applyAdj} disabled={adjBusy}
            className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
            {adjBusy ? "Applying…" : "Apply Adjustment"}
          </button>
          {adjLog.length > 0 && (
            <div className="space-y-1 pt-2 border-t border-white/5">
              {adjLog.map((l, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-white/30">
                  <span>{l.employee_id} · {l.leave_type}</span>
                  <span className={l.delta > 0 ? "text-[#00ff90]/60" : "text-red-400/60"}>{l.delta > 0 ? "+" : ""}{l.delta}d</span>
                  <span className="text-white/20">{l.at}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {/* Balance table */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Employee Balances</span>
          <div className="ml-auto flex items-center gap-2">
            <Search size={13} className="text-white/30" />
            <input className={inputCls} placeholder="Search name, ID, dept…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
          </div>
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0d0d0d]">
              <tr className="border-b border-white/5">
                {["Employee","Dept","Annual","Sick","Casual","Unpaid",""].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}><td colSpan={7} className="px-4 py-3"><div className="h-8 rounded bg-white/5 animate-pulse" /></td></tr>
                ))
              ) : visible.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/20 italic text-sm">No balance data.</td></tr>
              ) : visible.map(b => (
                <tr key={b.employee_id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-3 py-3">
                    <button onClick={() => onOpenEmployee?.(b.employee_id, b.name)}
                      className="text-left hover:text-[#00ff90] transition-colors">
                      <p className="text-sm text-white/80 font-medium">{b.name}</p>
                      <p className="text-xs text-white/30">{b.employee_id}</p>
                    </button>
                  </td>
                  <td className="px-3 py-3 text-white/40 text-xs">{b.dept}</td>
                  {["Annual","Sick","Casual","Unpaid"].map(t => {
                    const bal = b.balances?.find(x => x.type === t);
                    const rem = bal ? (bal.total - bal.used) : null;
                    const color = BALANCE_COLORS[t] || "#ffffff44";
                    return (
                      <td key={t} className="px-3 py-3">
                        {bal ? (
                          <div>
                            <span className="text-sm font-bold" style={{ color }}>{rem}</span>
                            <span className="text-xs text-white/25 ml-1">/ {bal.total}</span>
                          </div>
                        ) : <span className="text-white/20 text-xs">—</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-3">
                    <button onClick={() => onOpenEmployee?.(b.employee_id, b.name)}
                      className="text-xs px-2 py-1 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/50 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}